// src/utilities/eventUtils.ts
import moment from 'moment-timezone';
import { RRule, rrulestr } from 'rrule';
import { AllEvent } from '../interfaces/identity/calendar/Events';
import { Event } from '../interfaces/Event';

// Get the system/browser timezone
const getSystemTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
  } catch (e) {
    return 'America/Chicago';
  }
};

// Set the default timezone for all moment operations in this file
const SYSTEM_TIMEZONE = getSystemTimezone();
moment.tz.setDefault(SYSTEM_TIMEZONE);

export const expandRecurringEvents = (
  events: AllEvent[],
  viewStart: Date,
  viewEnd: Date
): Event[] => {
  const expandedEvents: Event[] = [];
  const exceptionMap = new Map<string, AllEvent>(); // Key: parentId-YYYY-MM-DD

  // First, map out all exceptions for quick lookup
  events.forEach(event => {
    if (event.parentEventId && event.exceptionDate) {
      // Use the timezone from the event if available, otherwise use system timezone
      const tz = event.time_zone || SYSTEM_TIMEZONE;
      const originalDateStr = moment.tz(event.exceptionDate, tz).format('YYYY-MM-DD');
      exceptionMap.set(`${event.parentEventId}-${originalDateStr}`, event);
    }
  });

  // Now, expand recurring master events and handle exceptions
  events.forEach(event => {
    if (event.recurrenceRule) {
      try {
        const tz = event.time_zone || SYSTEM_TIMEZONE;
        // Ensure the DTSTART in the rule has the correct timezone
        const ruleString = event.recurrenceRule.includes('DTSTART')
          ? event.recurrenceRule
          : `DTSTART;TZID=${tz}:${moment.tz(event.startTime, tz).format('YYYYMMDDTHHmmss')}\n${event.recurrenceRule}`;

        const rule = rrulestr(ruleString, { tzid: tz });
        const occurrences = rule.between(viewStart, viewEnd);
        const duration = moment(event.endTime).diff(moment(event.startTime));

        occurrences.forEach(occurrence => {
          const occurrenceMoment = moment.tz(occurrence, tz);
          const originalDateStr = occurrenceMoment.format('YYYY-MM-DD');
          
          const exception = exceptionMap.get(`${event.id}-${originalDateStr}`);
          
          if (exception) {
            // This occurrence is an exception, so we render the exception event instead
            expandedEvents.push({
              id: exception.id,
              title: exception.title,
              date: moment.tz(exception.startTime, tz).toDate(),
              elapsedTimeInMinutes: moment.duration(moment.tz(exception.endTime, tz).diff(moment.tz(exception.startTime, tz))).asMinutes(),
              color: exception.ownerColor || '#F4E8E6',
              recurrenceRule: exception.recurrenceRule,
              parentEventId: exception.parentEventId,
              time_zone: exception.time_zone,
              originalEventId: event.id, // The exception's originalEventId is the master event
            });
          } else {
            // This is a regular occurrence
            const start = occurrenceMoment.toDate();
            
            expandedEvents.push({
              id: `${event.id}-${start.toISOString()}`, // Unique ID for each occurrence
              title: event.title,
              date: start,
              elapsedTimeInMinutes: moment.duration(duration).asMinutes(),
              color: event.ownerColor || '#F4E8E6',
              recurrenceRule: event.recurrenceRule,
              parentEventId: event.id, // The occurrence's parent is the master
              time_zone: tz,
              originalEventId: event.id, // The original event is the master
            });
          }
        });
      } catch (e) {
        console.error("Error expanding rrule for event:", event.id, e);
        // If expansion fails, add the master event itself if it's within the view
        const eventStart = moment.tz(event.startTime, event.time_zone || SYSTEM_TIMEZONE);
        if (eventStart.isBetween(viewStart, viewEnd) && !event.parentEventId) {
            expandedEvents.push({
                id: event.id,
                title: event.title,
                date: eventStart.toDate(),
                elapsedTimeInMinutes: moment.duration(moment(event.endTime).diff(moment(event.startTime))).asMinutes(),
                color: event.ownerColor || '#F4E8E6',
                originalEventId: event.id,
                recurrenceRule: event.recurrenceRule,
                parentEventId: event.parentEventId,
                time_zone: event.time_zone,
            });
        }
      }
    } else if (!event.parentEventId) {
      // This is a non-recurring event, not an exception
      expandedEvents.push({
        id: event.id,
        title: event.title,
        date: moment.tz(event.startTime, event.time_zone || SYSTEM_TIMEZONE).toDate(),
        elapsedTimeInMinutes: moment.duration(moment(event.endTime).diff(moment(event.startTime))).asMinutes(),
        color: event.ownerColor || '#F4E8E6',
        originalEventId: event.id,
        recurrenceRule: event.recurrenceRule,
        parentEventId: event.parentEventId,
        time_zone: event.time_zone,
      });
    }
  });

  return expandedEvents;
};
